/**
 * StoresTable — the ONE stores table.
 *
 * Rendered by the Storage page (the project's full list) AND by an agent's
 * Settings → Storage section (just the stores THAT agent owns). Same columns,
 * same chips, same row click-through to the same store DETAIL PAGE
 * (/projects/:projectId/stores/:storeId) — so the two surfaces cannot drift
 * apart. A caller supplies the rows; everything else is this component's
 * business.
 *
 * Deliberately NO create form and NO row actions: stores are created
 * conversationally (ask the agent / Copilot), so the empty state points at chat
 * rather than offering a "+ New" button. Deletion lives on the detail page.
 */

import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import DataTable from '../DataTable/DataTable';
import { Stores } from '../../../services/stores';
import './StoresTable.css';

/** Human-readable byte count — shared by the table's SIZE column + the store
 * detail page's Size cell. */
export const fmtBytes = (n) => {
  if (n == null || Number.isNaN(Number(n))) return '—';
  const v = Number(n);
  if (v < 1024) return `${v} B`;
  if (v < 1024 * 1024) return `${(v / 1024).toFixed(1)} KB`;
  if (v < 1024 * 1024 * 1024) return `${(v / (1024 * 1024)).toFixed(1)} MB`;
  return `${(v / (1024 * 1024 * 1024)).toFixed(2)} GB`;
};

// Map a store's raw `status` onto the shared pipelines status-pill colour
// keys so we reuse the existing palette (no new colours):
//   ready / active / available → green ("Ready")
//   provisioning / creating / pending → amber ("Provisioning")
//   error / failed → red ("Error")
//   anything else / null → gray ("Unknown")
const STATUS_KEY = (status) => {
  const s = String(status || '').toLowerCase();
  if (s === 'ready' || s === 'active' || s === 'available' || s === 'running') return 'live';
  if (s === 'provisioning' || s === 'creating' || s === 'pending' || s === 'building') return 'building';
  if (s === 'error' || s === 'failed') return 'failed';
  return 'sleeping';
};
const STATUS_LABEL = {
  live: 'Ready',
  building: 'Provisioning',
  failed: 'Error',
  sleeping: 'Unknown',
};

export const StoreStatus = ({ status }) => {
  const key = STATUS_KEY(status);
  // If we got a real status string but it didn't map to a known key, show
  // the raw value (title-cased-ish) rather than a misleading "Unknown".
  const label = key === 'sleeping' && status ? String(status) : STATUS_LABEL[key];
  return (
    <span className={`pipelines-status pipelines-status-${key}`}>
      <span className="pipelines-status-dot" />
      {label}
    </span>
  );
};

// Compact storeId for the table: prefix up to the first `_` + the next 5 chars
// (e.g. store_cc6a8f083… → "store_cc6a8"). The full id lives on the row's
// detail page. Falls back to the first 5 chars for prefix-less ids.
export const shortStoreId = (storeId) => {
  if (!storeId) return '—';
  const i = storeId.indexOf('_');
  if (i === -1) return storeId.slice(0, 5);
  return storeId.slice(0, i + 1) + storeId.slice(i + 1, i + 6);
};

// Truncate the table's DESCRIPTION cell to a fixed character budget (not a
// width-dependent CSS clip) so it reads the same on any viewport. Cuts at the
// last word boundary within the budget when possible. The FULL text stays in
// the cell's `title` attr (hover) + the row's detail page.
const DESC_MAX = 76;
const truncateDescription = (text) => {
  const s = String(text || '').trim();
  if (s.length <= DESC_MAX) return s;
  const slice = s.slice(0, DESC_MAX);
  const lastSpace = slice.lastIndexOf(' ');
  const cut = lastSpace > DESC_MAX * 0.6 ? slice.slice(0, lastSpace) : slice;
  return `${cut.trimEnd()}…`;
};

// Columns: ID (copyable) / DESCRIPTION (the wide, primary column — what the
// store is FOR) / TYPE (chip) / SIZE (deferred live probe) / STATUS (colored
// dot). No NAME column: stores are created conversationally and have no
// meaningful name (it just echoes the type), so the storeId IS the identity.
// DESCRIPTION takes the flex space; the rest are pinned narrow.
// A function of `sizes` (the deferred ?withSize=1 backfill): null = probe
// still running (…), a map = probed (missing entry → "—").
const buildColumns = (sizes) => [
  {
    header: 'ID',
    key: 'storeId',
    width: '160px',
    className: 'store-col-id',
    // Hover shows the store's EXTERNAL resource name (rn — north-star #10)
    // when the API sends one; click copies it (falling back to the bare id on
    // an older backend). The rn is accepted back anywhere a storeId is.
    // stopPropagation: a copy click must not bubble into the row's
    // click-through to the detail page (copy would navigate away mid-gesture).
    render: (s) => (
      <code
        className="store-id-short"
        title={s.rn || s.storeId}
        style={{ cursor: 'copy' }}
        onClick={(e) => {
          e.stopPropagation();
          navigator.clipboard.writeText(s.rn || s.storeId).catch(() => {});
        }}
      >
        {shortStoreId(s.storeId)}
      </code>
    ),
  },
  {
    // No width → takes all remaining space (the wide, primary column).
    header: 'DESCRIPTION',
    key: 'description',
    className: 'store-col-desc',
    render: (s) =>
      s.description ? (
        <span className="store-description" title={s.description}>
          {truncateDescription(s.description)}
        </span>
      ) : (
        <span className="store-muted">—</span>
      ),
  },
  {
    header: 'TYPE',
    key: 'type',
    width: '110px',
    className: 'store-col-type',
    render: (s) =>
      s.type ? <span className="store-type-chip">{s.type}</span> : <span className="store-muted">—</span>,
  },
  {
    header: 'SIZE',
    key: 'sizeBytes',
    width: '100px',
    className: 'store-col-size',
    render: (s) => (
      sizes == null
        ? <span className="store-muted">…</span>
        : <span className="store-size">{fmtBytes(sizes[s.storeId])}</span>
    ),
  },
  {
    header: 'STATUS',
    key: 'status',
    width: '120px',
    className: 'store-col-status',
    render: (s) => <StoreStatus status={s.status} />,
  },
];

const StoresTable = ({ stores = [], loading = false, emptyState }) => {
  const navigate = useNavigate();
  const { projectId } = useParams();

  // SIZE backfill — mirrors the self-host table's two-call contract: the
  // table paints from the plain list first, then ONE ?withSize=1 request
  // (a live probe whose cost grows with the data) fills the column. Failure
  // leaves "—" everywhere, never an error state.
  const [sizes, setSizes] = useState(null);
  useEffect(() => {
    if (!projectId || !stores.length) return undefined;
    let alive = true;
    Stores.list(projectId, { withSize: true })
      .then((r) => {
        if (!alive) return;
        setSizes(Object.fromEntries((r?.stores || []).map((s) => [s.storeId, s.sizeBytes])));
      })
      .catch(() => { if (alive) setSizes({}); });
    return () => { alive = false; };
  }, [projectId, stores.length]); // eslint-disable-line react-hooks/exhaustive-deps

  const columns = useMemo(() => buildColumns(sizes), [sizes]);

  // Newest first — the row you just created is the one you're looking for.
  // (The registry Query returns storeId order, which reads as random.)
  const sorted = useMemo(
    () => [...stores].sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0)),
    [stores],
  );

  return (
    <DataTable
      data={sorted}
      columns={columns}
      loading={loading}
      rowKey="storeId"
      // Row click → the store DETAIL page (viewer + query + delete). Both
      // hosts of this table render under /projects/:projectId, so the param
      // is always resolvable here.
      onRowClick={(s) => navigate(`/projects/${projectId}/stores/${encodeURIComponent(s.storeId)}`)}
      emptyState={
        emptyState || {
          title: 'No stores yet',
          description: 'No stores yet — ask your agent or the Copilot to create one.',
        }
      }
    />
  );
};

export default StoresTable;
